import React, {
  useMemo, useState, useRef, useEffect,
} from 'react';
import { parse, isValid } from 'date-fns';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Button from '../../../../shared/_TBComponents/Button';
import Icon from '../../../../shared/_TBComponents/Icon';
import Modal from '../../../../shared/_TBComponents/Modal';
import Text from '../../../../shared/_TBComponents/Text';
import EasyJoinCards from './EasyJoinCards';
import EasyJoinModal from './EasyJoinModal';

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 515px;
  min-height: 100%;
  background: #fff;
  padding: 16px;
  margin: 0 auto;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const CreateButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 24px;
`;

const IntroSection = styled.div`
  border-bottom: 2px solid #E0E0E0;
  padding-bottom: 4px;
`;

const CodeExampleBlock = styled.div`
  background: #F6F6F6;
  border-radius: 4px;
  padding: 8px;
  margin: 8px 0 12px;
  display: inline-flex;
  gap: 60px;
  max-width: 300px;
`;

const CodeExampleField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CodeHistorySection = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 12px;
`;

const CodeHistoryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const SelectAllContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: ${({ itemsAreSelected }) => (itemsAreSelected ? '3px 8px' : '3px 2px')};
  border: 1px solid ${({ itemsAreSelected }) => (itemsAreSelected ? '#EEEEEE' : 'transparent')};
  border-radius: 4px;
  box-shadow: ${({ itemsAreSelected }) => (itemsAreSelected ? '0px 4px 8px 0px rgba(48, 49, 51, 0.10)' : 'none')};

  .checkbox-input {
    appearance: none;
    min-width: 18px;
    min-height: 18px;
    max-width: 18px;
    max-height: 18px;
    border: 2px solid ${({ isChecked }) => (isChecked ? '#77A7FF' : '#E0E0E0')};
    border-radius: 4px;
    outline: none;
    cursor: pointer;
    position: relative;
  }

  .checkbox-input:checked::after {
    content: '';
    position: absolute;
    top: 1px;
    left: 5px;
    width: 5px;
    height: 10px;
    border: solid #77A7FF;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
    display: block;
  }

  .checkbox-input:indeterminate {
    border-color: #77A7FF;
  }

  .checkbox-input:indeterminate::after {
    content: '-';
    position: absolute;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 31px;
    color: #77A7FF;
    margin-top: 3px;
  }
`;

const CodeHistoryHeaderActions = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 6px;
  margin-left: auto;
`;

const BulkDeleteButton = styled.button`
  background: none;
  border: none;
  padding: 4px 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background 0.15s;

  &:hover:not(:disabled) {
    background: #FFF0F0;
    svg path { fill: #FF0505 !important; }
  }
  &:disabled { cursor: default; }
`;

const SortButton = styled.button`
  background: none;
  border: none;
  padding: 4px 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background 0.15s;

  &:hover {
    background: rgba(119, 167, 255, 0.12);
    svg path { fill: #77A7FF !important; }
  }
`;

const SortWrapper = styled.div`
  position: relative;
`;

const SortDropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background: white;
  border: 1px solid #E0E0E0;
  border-radius: 4px;
  box-shadow: 0 16px 24px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05);
  padding: 6px;
  z-index: 200;
  min-width: 170px;
`;

const SortRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: ${({ isActive }) => (isActive ? 700 : 400)};
  color: ${({ isActive }) => (isActive ? '#77A7FF' : '#333333')};
  transition: background 0.15s;

  &:hover { background: #F5F5F5; }
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 100%;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 16px;
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const EasyJoinPage = ({
  codes,
  calendars,
  groups,
  isLoadingCodes,
  isCreating,
  isUpdating,
  isDeleting,
  lastCreated,
  lastUpdated,
  onBack,
  backLabel,
  onCreate,
  onUpdate,
  onDelete,
  onClearLastCreated,
  onClearLastUpdated,
}) => {
  const [editingCode, setEditingCode] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [sortField, setSortField] = useState('created');
  const [sortDir, setSortDir] = useState('desc');
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [newlyCreatedId, setNewlyCreatedId] = useState(null);

  const sortRef = useRef(null);
  const selectAllCheckboxRef = useRef(null);

  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      const { current } = selectAllCheckboxRef;
      current.indeterminate = selectedIds.size > 0 && selectedIds.size < codes.length;
    }
  }, [selectedIds.size, codes.length]);

  useEffect(() => {
    if (lastCreated) {
      setIsModalOpen(false);
      setEditingCode(null);
      onClearLastCreated();
      setNewlyCreatedId(lastCreated.id);
    }
  }, [lastCreated]);

  useEffect(() => {
    if (lastUpdated) {
      setIsModalOpen(false);
      setEditingCode(null);
      onClearLastUpdated();
    }
  }, [lastUpdated]);

  useEffect(() => {
    if (!isSortOpen) return undefined;
    const handleClick = (e) => {
      if (sortRef.current && !sortRef.current.contains(e.target)) {
        setIsSortOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isSortOpen]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // Sort codes by id (creation order), expiry date, or name depending on the selected sort option
  const sortedCodes = useMemo(() => [...codes].sort((a, b) => {
    if (sortField === 'created') {
      return sortDir === 'desc' ? b.id - a.id : a.id - b.id;
    }
    if (sortField === 'expiry') {
      const toTime = (date) => {
        if (!date) return sortDir === 'asc' ? Infinity : -Infinity;
        const parsed = parse(date, 'MMMM d, yyyy', new Date());
        if (!isValid(parsed)) return sortDir === 'asc' ? Infinity : -Infinity;
        return parsed.getTime();
      };
      return sortDir === 'asc'
        ? toTime(a.expirationDate) - toTime(b.expirationDate)
        : toTime(b.expirationDate) - toTime(a.expirationDate);
    }
    const aVal = sortField === 'name' ? (a.description || '').toLowerCase() : '';
    const bVal = sortField === 'name' ? (b.description || '').toLowerCase() : '';
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  }), [codes, sortField, sortDir]);

  // Pin newly created code to top regardless of current sort
  const displayedCodes = useMemo(() => {
    if (!newlyCreatedId) return sortedCodes;
    const idx = sortedCodes.findIndex((code) => code.id === newlyCreatedId);
    if (idx <= 0) return sortedCodes;
    const reordered = [...sortedCodes];
    const [pinned] = reordered.splice(idx, 1);
    reordered.unshift(pinned);
    return reordered;
  }, [sortedCodes, newlyCreatedId]);

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  };

  const handleSelectAll = () => setSelectedIds(new Set(codes.map((c) => c.id)));
  const handleSelectNone = () => setSelectedIds(new Set());

  const handleBulkDelete = () => {
    onDelete([...selectedIds]);
    setSelectedIds(new Set());
    setShowBulkDeleteModal(false);
  };

  const handleOpenCreate = () => {
    setEditingCode(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setEditingCode(null);
    setIsModalOpen(false);
  };

  const handleEdit = (code) => {
    setEditingCode(code);
    setIsModalOpen(true);
  };

  return (
    <PageWrapper>
      {onBack && (
        <ButtonWrapper>
          <Button
            name={backLabel || 'Back'}
            icon='left-arrow'
            iconSize={8}
            iconColor='#9E9E9E'
            bgColor='transparent'
            noBorder
            txtColor='#9E9E9E'
            customBorderRadius='4px'
            styledTextProps={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.3px' }}
            styledWrapperProps={{ paddingLeft: '0', marginBottom: '4px' }}
            onClick={onBack}
          />
        </ButtonWrapper>
      )}
      <IntroSection>
        <Text
          styledTextProps={{ fontSize: '24px', fontWeight: 900, color: '#333333' }}
          styledWrapperProps={{ marginBottom: '8px' }}
        >
          EasyJoin
        </Text>
        <Text
          styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}
          styledWrapperProps={{ marginBottom: '4px' }}
        >
          EasyJoin codes make it easy for your athletes to create accounts and start
          using the app. They look like this:
        </Text>
        <CodeExampleBlock>
          <CodeExampleField>
            <Text
              styledTextProps={{
                fontSize: '12px', fontWeight: 700, color: '#9E9E9E',
              }}
              upperCase
            >
              Code:
            </Text>
            <Text styledTextProps={{
              fontSize: '16px', fontWeight: 700, color: '#444',
            }}
            >
              WTSY-ZFAK
            </Text>
          </CodeExampleField>
          <CodeExampleField>
            <Text
              styledTextProps={{
                fontSize: '12px', fontWeight: 700, color: '#9E9E9E',
              }}
              upperCase
            >
              Password:
            </Text>
            <Text
              styledTextProps={{
                fontSize: '16px', fontWeight: 700, color: '#444',
              }}
            >
              JBC2IUFN
            </Text>
          </CodeExampleField>
        </CodeExampleBlock>
        <Text
          styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}
          styledWrapperProps={{ marginBottom: '32px' }}
        >
          When you create an EasyJoin code, you can set the calendar/groups the athletes
          should be assigned to. Then, just give them the code and tell them to enter it
          into the app to get started.
        </Text>
        <CreateButtonRow>
          <Button
            name='Create an EasyJoin Code'
            fontSize='14px'
            bgColor='#444'
            txtColor='#ffffff'
            borderColor='#444'
            customBorderRadius='4px'
            styledTextProps={{ fontWeight: 700 }}
            icon='plus'
            iconSize={12}
            iconColor='#ffffff'
            onClick={handleOpenCreate}
          />
        </CreateButtonRow>
      </IntroSection>

      <CodeHistorySection>
        <CodeHistoryHeader>
          <SelectAllContainer
            itemsAreSelected={selectedIds.size > 0}
            isChecked={selectedIds.size === codes.length && codes.length > 0}
          >
            <input
              ref={selectAllCheckboxRef}
              className='checkbox-input'
              type='checkbox'
              checked={selectedIds.size === codes.length && codes.length > 0}
              onChange={() => {
                if (selectedIds.size > 0) {
                  handleSelectNone();
                } else {
                  handleSelectAll();
                }
              }}
            />
            <Text styledTextProps={{
              fontSize: selectedIds.size > 0 ? '19px' : '16px',
              fontWeight: selectedIds.size > 0 ? 700 : 400,
              color: selectedIds.size > 0 ? '#77A7FF' : '#BDBDBD',
            }}
            >
              {`Selected (${selectedIds.size})`}
            </Text>
          </SelectAllContainer>
          <CodeHistoryHeaderActions>
            <BulkDeleteButton
              type='button'
              title='Delete selected'
              disabled={selectedIds.size === 0}
              onClick={() => setShowBulkDeleteModal(true)}
            >
              <Icon icon='delete' size={20} color='#BDBDBD' />
            </BulkDeleteButton>
            <SortWrapper ref={sortRef}>
              <SortButton type='button' onClick={() => setIsSortOpen((p) => !p)}>
                <Icon icon='Arrows-Sort' size={20} color='#BDBDBD' />
              </SortButton>
              {isSortOpen && (
                <SortDropdown>
                  {[
                    { field: 'created', label: 'Created Date' },
                    { field: 'expiry', label: 'Expiry Date' },
                    { field: 'name', label: 'Name' },
                  ].map(({ field, label }) => (
                    <SortRow
                      key={field}
                      isActive={sortField === field}
                      onClick={() => handleSort(field)}
                    >
                      {label}
                      {sortField === field && (
                        <Icon
                          icon={sortDir === 'asc' ? 'alt-up-arrow' : 'alt-down-arrow'}
                          size={14}
                          color='#77A7FF'
                        />
                      )}
                    </SortRow>
                  ))}
                </SortDropdown>
              )}
            </SortWrapper>
          </CodeHistoryHeaderActions>
        </CodeHistoryHeader>
        <EasyJoinCards
          codes={displayedCodes}
          isLoadingCodes={isLoadingCodes}
          isDeleting={isDeleting}
          onEdit={handleEdit}
          onDelete={onDelete}
          selectedIds={selectedIds}
          onToggleStar={handleToggleSelect}
          newlyCreatedId={newlyCreatedId}
        />
      </CodeHistorySection>

      {/* Add/Edit Code Modal */}
      <EasyJoinModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        editingCode={editingCode}
        groups={groups}
        calendars={calendars}
        isCreating={isCreating}
        isUpdating={isUpdating}
        onCreate={onCreate}
        onUpdate={onUpdate}
      />

      {/* Bulk Delete Modal */}
      <Modal
        isOpen={showBulkDeleteModal}
        onRequestClose={() => setShowBulkDeleteModal(false)}
        customBorderRadius='8px'
        customPadding='24px'
        customMaxWidth='600px'
        customMinHeight='200px'
      >
        <ModalContent>
          <ModalHeader>
            <Text styledTextProps={{ fontSize: '24px', fontWeight: 700 }}>
              {`Delete ${selectedIds.size} Easy Join Code${selectedIds.size !== 1 ? 's' : ''}`}
            </Text>
            <Button
              icon='remove'
              iconOnly
              noBorder
              txtColor='#9E9E9E'
              onClick={() => setShowBulkDeleteModal(false)}
            />
          </ModalHeader>
          <Text
            styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}
            styledWrapperProps={{ marginBottom: '24px' }}
          >
            Are you sure you want to delete these EasyJoin codes?
            Athletes will no longer be able to use them to join.
          </Text>
          <ModalFooter style={{ flexDirection: 'column', gap: '8px' }}>
            <Button
              name={isDeleting ? 'Deleting...' : 'Delete'}
              fontSize='14px'
              bgColor='#FF0505'
              txtColor='#FFF'
              borderColor='#FF0505'
              customBorderRadius='6px'
              fullWidth
              styledTextProps={{ fontWeight: 600 }}
              disabled={isDeleting}
              onClick={handleBulkDelete}
            />
            <Button
              name='Cancel'
              fontSize='14px'
              bgColor='transparent'
              noBorder
              txtColor='#444444'
              customBorderRadius='6px'
              fullWidth
              styledTextProps={{ fontWeight: 600 }}
              onClick={() => setShowBulkDeleteModal(false)}
            />
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageWrapper>
  );
};

EasyJoinPage.propTypes = {
  codes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    joinCode: PropTypes.string,
    joinPassword: PropTypes.string,
    description: PropTypes.string,
    expirationDate: PropTypes.string,
    startDate: PropTypes.number,
    endDate: PropTypes.number,
    dateOffset: PropTypes.number,
    groups: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    })),
    calendar: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
  })).isRequired,
  calendars: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
  ).isRequired,
  groups: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
  ).isRequired,
  isLoadingCodes: PropTypes.bool.isRequired,
  isCreating: PropTypes.bool.isRequired,
  isUpdating: PropTypes.bool.isRequired,
  isDeleting: PropTypes.bool.isRequired,
  lastCreated: PropTypes.shape({
    id: PropTypes.number,
    joinCode: PropTypes.string,
    joinPassword: PropTypes.string,
    description: PropTypes.string,
    expirationDate: PropTypes.string,
    groups: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
    calendar: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
  }),
  onBack: PropTypes.func,
  backLabel: PropTypes.string,
  onCreate: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onClearLastCreated: PropTypes.func.isRequired,
  lastUpdated: PropTypes.shape({ id: PropTypes.number }),
  onClearLastUpdated: PropTypes.func.isRequired,
};

EasyJoinPage.defaultProps = {
  lastCreated: null,
  lastUpdated: null,
  onBack: null,
  backLabel: null,
};

export default EasyJoinPage;
